'use client';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Spinner } from '@phosphor-icons/react';
import { authApi } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/Button';

export function WeChatCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('正在处理微信登录...');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    // 检查是否有错误
    if (error) {
      setStatus('error');
      setMessage('用户取消授权或授权失败');
      return;
    }

    // 检查必要参数
    if (!code || !state) {
      setStatus('error');
      setMessage('缺少必要的授权参数');
      return;
    }

    // 验证 state（可选的安全检查）
    const savedState = sessionStorage.getItem('wechat_state');
    if (savedState && savedState !== state) {
      setStatus('error');
      setMessage('安全验证失败，请重新登录');
      return;
    }

    // 清除保存的 state
    sessionStorage.removeItem('wechat_state');

    // 调用后端完成登录
    handleWeChatLogin(code, state);
  }, [searchParams]);

  const handleWeChatLogin = async (code: string, state: string) => {
    try {
      const response = await authApi.weChatCallback(code, state);
      
      if (response.code !== 0 || !response.data) {
        throw new Error(response.message || '微信登录失败');
      }

      const { user, tokens } = response.data;

      // 使用返回的 token 登录
      login(
        {
          id: user.id,
          username: user.username,
          email: user.email,
          role: user.role,
        },
        tokens.access_token
      );

      setStatus('success');
      setMessage('登录成功，正在跳转...');

      // 延迟跳转到首页
      setTimeout(() => {
        navigate('/home', { replace: true });
      }, 1500);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : '微信登录失败';
      setStatus('error');
      setMessage(errorMsg);
    }
  };

  const handleRetry = () => {
    navigate('/login', { replace: true });
  };

  const handleGoHome = () => {
    navigate('/home', { replace: true });
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* 状态图标 */}
          <div className="mb-6">
            {status === 'loading' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto rounded-full bg-blue-50 flex items-center justify-center"
              >
                <Spinner className="w-10 h-10 text-blue-600 animate-spin" />
              </motion.div>
            )}
            {status === 'success' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto rounded-full bg-green-50 flex items-center justify-center"
              >
                <CheckCircle className="w-10 h-10 text-green-600" weight="fill" />
              </motion.div>
            )}
            {status === 'error' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto rounded-full bg-red-50 flex items-center justify-center"
              >
                <XCircle className="w-10 h-10 text-red-600" weight="fill" />
              </motion.div>
            )}
          </div>

          {/* 标题 */}
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">
            {status === 'loading' && '微信登录中'}
            {status === 'success' && '登录成功'}
            {status === 'error' && '登录失败'}
          </h1>

          {/* 消息 */}
          <p className="text-zinc-500 mb-8">{message}</p>

          {/* 操作按钮 */}
          {status !== 'loading' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex gap-3 justify-center"
            >
              {status === 'error' && (
                <Button onClick={handleRetry} variant="secondary">
                  返回登录页
                </Button>
              )}
              {status === 'success' && (
                <Button onClick={handleGoHome} variant="primary">
                  进入首页
                </Button>
              )}
            </motion.div>
          )}
        </div>

        {/* 底部版权 */}
        <p className="text-center text-xs text-zinc-400 mt-8">
          2026 DataFlow Engine (诸暨途源科技有限公司). All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}
